/**
 * Store update prompt.
 *
 * The version policy is still the source of truth for "this build is old",
 * but the user-facing Android UX should come from Google Play whenever Play
 * can prove an update is actually available for this install.
 */
import { useEffect, useRef } from 'react'
import { Alert, Linking, Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import { requireOptionalNativeModule } from 'expo'
import type { MandatoryUpdateGate } from './use-mandatory-update-gate'
import { isPlayStoreAppNotOwnedError } from '../errors/service-error'
import { addBreadcrumb, captureError } from '../utils/observability'
import { notification, NotificationType } from '../utils/haptics'

type InAppUpdatesModule = {
  FLEXIBLE?: number
  IMMEDIATE?: number
  checkForUpdate: () => Promise<{
    updateAvailable: boolean
    updateInProgress?: boolean
    immediateAllowed?: boolean
    flexibleAllowed?: boolean
    storeVersion?: string
  }>
  startUpdate: (updateType?: number) => Promise<boolean>
}

const STORE_UPDATE_PROMPT_COOLDOWN_MS = 30 * 60 * 1000
const storeUpdatePromptAttempts = new Map<string, number>()
const startedNativeUpdatePromptKeys = new Set<string>()

export function useStoreUpdatePrompt(gate: MandatoryUpdateGate): void {
  const { t } = useTranslation()
  const prompting = useRef(false)
  const lastPromptKey = useRef<string | null>(null)

  useEffect(() => {
    if (__DEV__ || Platform.OS === 'web') return

    /*
     * DOS MODOS, Y HASTA HOY SOLO CORRIA UNO.
     *
     * Este hook --que ya sabia usar Play In-App Updates, o sea descargar en
     * segundo plano sin sacar a nadie de la app-- arrancaba con
     * `if (gate.status !== 'store-required') return`. Y `store-required` solo
     * existe cuando el muro (TAGO_ENFORCE_STORE_UPDATE) esta encendido, que vive
     * apagado y DEBE seguir apagado: encendido de mas ya encerro gente el 23/08
     * contra un build que Play todavia no servia, y apaga el OTA justo de los
     * encerrados.
     *
     * O sea: todo este mecanismo era codigo muerto, y quien estaba en un build
     * viejo nunca se enteraba de que existia uno nuevo. Eso no es neutral --
     * `runtimeVersion` es por huella nativa, asi que un build viejo vive en su
     * propio silo y NINGUN arreglo por aire lo alcanza. Callarse la
     * actualizacion los dejaba incomunicados de todo hotfix futuro.
     *
     * Ahora corre tambien cuando simplemente HAY algo mas nuevo. Pero el aviso
     * NO usa el flujo nativo de Play, y esa correccion vale la pena explicarla
     * porque yo mismo escribi acá lo contrario:
     *
     *   "elige FLEXIBLE: Play baja la actualizacion de fondo y ofrece reiniciar
     *    cuando termina. Nadie pierde lo que estaba haciendo."
     *
     * Es FALSO, y el codigo nativo de la libreria lo dice textual
     * (expo-in-app-updates 0.9.0,
     * android/src/main/java/expo/modules/inappupdates/ExpoInAppUpdatesModule.kt:31):
     *
     *   private val listener = InstallStateUpdatedListener { state ->
     *     if (state.installStatus() == InstallStatus.DOWNLOADED) {
     *       appUpdateManager.completeUpdate()
     *     }
     *   }
     *
     * `completeUpdate()` REINSTALA Y REINICIA la app en cuanto termina la
     * descarga. No ofrece nada, no pregunta. O sea que un FLEXIBLE "amable"
     * puede reiniciar la app encima de alguien que va por la foto catorce de
     * una entrega, en la calle, junto al carro. Un guardia de ruta tampoco
     * alcanza: la descarga termina cuando termina, y para entonces la persona
     * ya pudo haber entrado a la entrega.
     *
     * Asi que el aviso opcional es un dialogo que NO bloquea y NO puede
     * reiniciar nada: "Despues" / "Abrir tienda". El flujo nativo queda solo
     * para el caso OBLIGATORIO, donde tomar la pantalla y reiniciar es
     * exactamente la intencion.
     */
    const esObligatorio = gate.status === 'store-required'
    if (!esObligatorio && gate.updateAvailableBuildVersion === null) return

    const promptKey = `${gate.currentBuildVersion}:${
      esObligatorio ? (gate.requiredBuildVersion ?? 0) : (gate.updateAvailableBuildVersion ?? 0)
    }:${gate.storeUrl ?? ''}`
    if (
      prompting.current ||
      lastPromptKey.current === promptKey ||
      hasRecentlyAttemptedStoreUpdatePrompt(promptKey) ||
      startedNativeUpdatePromptKeys.has(promptKey)
    ) {
      return
    }

    prompting.current = true
    lastPromptKey.current = promptKey
    rememberStoreUpdatePromptAttempt(promptKey)

    const openStore = () => {
      if (!gate.storeUrl) return
      void Linking.openURL(gate.storeUrl)
    }

    const showFallbackPrompt = () => {
      void notification(NotificationType.Warning)
      Alert.alert(
        esObligatorio ? t('updates.store_required_title') : t('updates.store_available_title'),
        esObligatorio ? t('updates.store_prompt_body') : t('updates.store_available_body'),
        [
          {
            text: t('updates.later'),
            style: 'cancel',
          },
          {
            text: t('updates.open_store'),
            onPress: openStore,
          },
        ],
      )
    }

    const run = async () => {
      try {
        if (Platform.OS === 'android') {
          const inAppUpdates = requireOptionalNativeModule<InAppUpdatesModule>('ExpoInAppUpdates')

          if (!inAppUpdates) {
            addBreadcrumb('updates', 'Play in-app update module unavailable in this build', {
              requiredBuildVersion: gate.requiredBuildVersion,
              currentBuildVersion: gate.currentBuildVersion,
            })
            showFallbackPrompt()
            return
          }

          const update = await inAppUpdates.checkForUpdate()

          addBreadcrumb('updates', 'Checked native Play in-app update availability', {
            updateAvailable: update.updateAvailable,
            updateInProgress: update.updateInProgress,
            immediateAllowed: update.immediateAllowed,
            flexibleAllowed: update.flexibleAllowed,
            storeVersion: update.storeVersion,
          })

          if (update.updateInProgress) {
            addBreadcrumb('updates', 'Native Play in-app update already in progress', {
              requiredBuildVersion: gate.requiredBuildVersion,
              currentBuildVersion: gate.currentBuildVersion,
            })
            return
          }

          if (update.updateAvailable) {
            void notification(NotificationType.Warning)
            const immediate = update.immediateAllowed !== false
            const flexible = update.flexibleAllowed !== false

            /*
             * AVISAR NO PUEDE REINICIAR LA APP.
             *
             * Cualquier flujo nativo de Play --FLEXIBLE incluido-- termina en
             * `completeUpdate()`, que reinstala y reinicia sin preguntar (ver
             * la nota larga arriba, con el codigo de la libreria). Para una
             * mejora opcional eso es inaceptable: la persona puede estar a
             * media entrega.
             *
             * El aviso opcional es un dialogo comun, con "Despues". Cuesta un
             * toque y no puede costar el trabajo de nadie.
             */
            if (!esObligatorio) {
              addBreadcrumb('updates', 'Advisory store update: prompting instead of native flow', {
                currentBuildVersion: gate.currentBuildVersion,
                updateAvailableBuildVersion: gate.updateAvailableBuildVersion,
              })
              showFallbackPrompt()
              return
            }

            const puedeArrancar = immediate || flexible
            if (puedeArrancar) {
              // Acá `esObligatorio` ya es true por la salida de arriba: dejarlo
              // en la condición sugeriría que hay un camino opcional que llega
              // hasta este punto, y no lo hay.
              const updateType = immediate ? inAppUpdates.IMMEDIATE : inAppUpdates.FLEXIBLE
              startedNativeUpdatePromptKeys.add(promptKey)

              try {
                const started = await inAppUpdates.startUpdate(updateType)
                addBreadcrumb('updates', 'Started native Play in-app update flow', {
                  updateType: immediate ? 'IMMEDIATE' : 'FLEXIBLE',
                  started,
                  requiredBuildVersion: gate.requiredBuildVersion,
                  currentBuildVersion: gate.currentBuildVersion,
                })
                if (started) return
              } catch (startError) {
                if (isPlayCoreSendIntentException(startError)) {
                  addBreadcrumb('updates', 'Skipped duplicate Play in-app update intent', {
                    requiredBuildVersion: gate.requiredBuildVersion,
                    currentBuildVersion: gate.currentBuildVersion,
                    message: startError instanceof Error ? startError.message : String(startError),
                  })
                  showFallbackPrompt()
                  return
                }

                throw startError
              }

              addBreadcrumb('updates', 'Native Play in-app update flow did not start', {
                requiredBuildVersion: gate.requiredBuildVersion,
                currentBuildVersion: gate.currentBuildVersion,
              })
              showFallbackPrompt()
              return
            }
          }

          addBreadcrumb('updates', 'Native Play update not available yet', {
            requiredBuildVersion: gate.requiredBuildVersion,
            currentBuildVersion: gate.currentBuildVersion,
          })
          return
        }

        showFallbackPrompt()
      } catch (error) {
        if (isPlayStoreAppNotOwnedError(error)) {
          addBreadcrumb('updates', 'Play in-app update unavailable for non-Play install', {
            requiredBuildVersion: gate.requiredBuildVersion,
            currentBuildVersion: gate.currentBuildVersion,
          })
          showFallbackPrompt()
          return
        }

        captureError(
          error,
          { screen: 'root', action: 'store-update-prompt' },
          {
            requiredBuildVersion: gate.requiredBuildVersion,
            currentBuildVersion: gate.currentBuildVersion,
          },
        )
        showFallbackPrompt()
      } finally {
        prompting.current = false
      }
    }

    void run()
  }, [
    gate.currentBuildVersion,
    gate.requiredBuildVersion,
    gate.status,
    gate.storeUrl,
    gate.updateAvailableBuildVersion,
    t,
  ])
}

function hasRecentlyAttemptedStoreUpdatePrompt(promptKey: string): boolean {
  const attemptedAt = storeUpdatePromptAttempts.get(promptKey)
  return Boolean(attemptedAt && Date.now() - attemptedAt < STORE_UPDATE_PROMPT_COOLDOWN_MS)
}

function rememberStoreUpdatePromptAttempt(promptKey: string): void {
  const now = Date.now()
  storeUpdatePromptAttempts.set(promptKey, now)

  for (const [key, attemptedAt] of storeUpdatePromptAttempts.entries()) {
    if (now - attemptedAt >= STORE_UPDATE_PROMPT_COOLDOWN_MS) {
      storeUpdatePromptAttempts.delete(key)
    }
  }
}

function isPlayCoreSendIntentException(error: unknown): boolean {
  const haystack = collectErrorText(error).join('\n')
  return (
    /SendIntentException/i.test(haystack) ||
    /startIntentSenderForResult/i.test(haystack) ||
    /startUpdateFlowForResult/i.test(haystack) ||
    /PendingIntent.*FLAG_ONE_SHOT/i.test(haystack)
  )
}

function collectErrorText(error: unknown, seen = new Set<unknown>()): string[] {
  if (!error || seen.has(error)) return []
  seen.add(error)

  if (typeof error === 'string') return [error]
  if (typeof error !== 'object') return [String(error)]

  const item = error as {
    name?: unknown
    message?: unknown
    code?: unknown
    status?: unknown
    cause?: unknown
  }
  return [item.name, item.message, item.code, item.status, ...collectErrorText(item.cause, seen)]
    .filter(
      (value): value is string | number => typeof value === 'string' || typeof value === 'number',
    )
    .map(String)
}
